interface CalendarMonthViewProps {
  selectedDate: Date;
  appointments: any[];
  onAppointmentClick: (appointment: any) => void;
  onDateClick: (date: Date) => void;
}

export function CalendarMonthView({
  selectedDate,
  appointments,
  onAppointmentClick,
  onDateClick,
}: CalendarMonthViewProps) {
  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((apt) => {
      const aptDate = new Date(apt.startTime);
      return (
        aptDate.getDate() === date.getDate() &&
        aptDate.getMonth() === date.getMonth() &&
        aptDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get upcoming appointments (from today onwards, sorted by date)
  const getUpcomingAppointments = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    return appointments
      .filter((apt) => {
        const aptDate = new Date(apt.startTime);
        const aptDateOnly = new Date(aptDate.getFullYear(), aptDate.getMonth(), aptDate.getDate());
        return aptDateOnly >= now;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 5); // Show up to 5 upcoming appointments
  };

  const upcomingAppointments = getUpcomingAppointments();

  return (
    <div className="p-2 sm:p-4">
      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-px mb-1 sm:mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 py-1 sm:py-2"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.substring(0, 1)}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        {days.map((date, index) => {
          if (!date) {
            return (
              <div
                key={`empty-${index}`}
                className="bg-gray-50 min-h-[60px] sm:min-h-[100px] lg:min-h-[120px]"
              />
            );
          }

          const dayAppointments = getAppointmentsForDate(date);
          const today = isToday(date);

          return (
            <div
              key={date.toISOString()}
              className={`bg-white min-h-[60px] sm:min-h-[100px] lg:min-h-[120px] p-1 sm:p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                today ? 'ring-2 ring-aalaw-red ring-inset' : ''
              }`}
              onClick={() => onDateClick(date)}
            >
              <div
                className={`text-xs sm:text-sm font-semibold mb-0.5 sm:mb-1 ${
                  today
                    ? 'text-gray-900'
                    : 'text-gray-900'
                }`}
                style={today ? { color: '#C41E3A' } : {}}
              >
                {date.getDate()}
              </div>

              {/* Appointments for this day - compact bars like Outlook */}
              <div className="space-y-0.5">
                {/* Mobile: Show only dots for appointments */}
                <div className="sm:hidden">
                  {dayAppointments.length > 0 && (
                    <div className="flex flex-wrap gap-0.5">
                      {dayAppointments.slice(0, 6).map((apt) => (
                        <div
                          key={apt.id}
                          className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAppointmentClick(apt);
                          }}
                          title={apt.title}
                        />
                      ))}
                      {dayAppointments.length > 6 && (
                        <span className="text-[8px] text-[#C41E3A] font-bold ml-0.5">
                          +{dayAppointments.length - 6}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Desktop: Show appointment bars */}
                <div className="hidden sm:block space-y-0.5">
                  {dayAppointments.slice(0, 4).map((apt) => {
                    const startTime = new Date(apt.startTime);
                    const timeStr = startTime.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });

                    return (
                      <div
                        key={apt.id}
                        className="text-[10px] bg-[#C41E3A] text-white px-1.5 py-0.5 rounded cursor-pointer hover:bg-[#8B0000] transition-colors flex items-center gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                        title={`${timeStr} - ${apt.title}`}
                      >
                        <span className="font-medium">{timeStr}</span>
                        <span className="truncate flex-1">{apt.title}</span>
                      </div>
                    );
                  })}
                  {dayAppointments.length > 4 && (
                    <button
                      className="text-[10px] text-[#C41E3A] font-semibold hover:underline w-full text-left px-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDateClick(date);
                      }}
                    >
                      +{dayAppointments.length - 4} more
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming Appointments List */}
      {upcomingAppointments.length > 0 && (
        <div className="mt-4 sm:mt-6">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 px-1">
            Upcoming Appointments
          </h3>
          <div className="space-y-2">
            {upcomingAppointments.map((apt) => {
              const startTime = new Date(apt.startTime);
              const endTime = new Date(apt.endTime);
              const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
              const isToday = (() => {
                const today = new Date();
                return (
                  startTime.getDate() === today.getDate() &&
                  startTime.getMonth() === today.getMonth() &&
                  startTime.getFullYear() === today.getFullYear()
                );
              })();

              return (
                <div
                  key={apt.id}
                  className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3 hover:shadow-md transition-all cursor-pointer hover:border-[#C41E3A]"
                  onClick={() => onAppointmentClick(apt)}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    {/* Date Badge */}
                    <div className={`flex-shrink-0 text-center ${isToday ? 'bg-[#C41E3A]' : 'bg-gray-100'} rounded-lg p-1.5 sm:p-2 min-w-[50px] sm:min-w-[60px]`}>
                      <div className={`text-[10px] sm:text-xs font-medium uppercase ${isToday ? 'text-white' : 'text-gray-600'}`}>
                        {startTime.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className={`text-lg sm:text-2xl font-bold ${isToday ? 'text-white' : 'text-gray-900'}`}>
                        {startTime.getDate()}
                      </div>
                      <div className={`text-[9px] sm:text-[10px] ${isToday ? 'text-white' : 'text-gray-500'}`}>
                        {startTime.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                    </div>

                    {/* Appointment Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                          {apt.title}
                        </h4>
                        {isToday && (
                          <span className="flex-shrink-0 text-[9px] sm:text-[10px] bg-[#C41E3A] text-white px-1.5 sm:px-2 py-0.5 rounded-full font-medium">
                            Today
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-1 text-[10px] sm:text-xs text-gray-600 flex-wrap">
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">
                            {startTime.toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <span>{duration} min</span>
                        
                        {apt.location && (
                          <>
                            <span className="text-gray-400 hidden sm:inline">•</span>
                            <div className="flex items-center gap-0.5 sm:gap-1 truncate">
                              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="truncate">{apt.location}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {apt.description && (
                        <p className="mt-1.5 text-[10px] sm:text-xs text-gray-600 line-clamp-1">
                          {apt.description}
                        </p>
                      )}

                      {/* Attendees */}
                      {apt.attendees && apt.attendees.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-[10px] sm:text-xs text-gray-600 truncate">
                            {apt.attendees.map((attendee: any) => `${attendee.firstName} ${attendee.lastName}`).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Sync Status */}
                    {apt.googleCalendarEventId && (
                      <div className="flex-shrink-0">
                        <div className="flex items-center gap-0.5 text-[9px] sm:text-[10px] text-green-700 bg-green-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="hidden sm:inline">Synced</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
